import * as React from 'react';

import { Badge as ShadcnBadge } from '@/hen-ui/shadcn/badge';
import { badgeVariants, type BadgeVariantProps } from './badge.variants';
import { cn } from '@/hen-ui/utils';

/**
 * Props for the Badge component
 *
 * Extends shadcn/ui Badge props with HEN Design System variants.
 * The Badge component wraps the shadcn/ui Badge primitive and adds HEN-specific
 * styling through the badgeVariants CVA function.
 */
export interface BadgeProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof ShadcnBadge>,
      'variant' | 'size'
    >,
    BadgeVariantProps {}

/**
 * Badge - A versatile label component for displaying metadata and categories
 *
 * A thin wrapper around shadcn/ui Badge with HEN-specific variants and design tokens.
 * Badges are used to label, categorize, or organize content with keywords that describe them.
 * All styling uses design tokens from tailwind.config.ts.
 *
 * ## Component Implementation
 * - Wraps shadcn/ui Badge component (from @/shadcn/badge)
 * - Uses Class Variance Authority (CVA) for variant management
 * - Supports asChild pattern for polymorphic behavior (inherited from shadcn)
 * - Uses React.forwardRef for proper ref forwarding
 *
 * ## Variants
 * - **primary** (default): Bold, dark purple background (bg-primary-900) with white text - use for emphasis
 * - **light**: Light background (bg-neutral-100) with purple text (text-primary-700) - use for secondary labels
 * - **neutral**: Subtle gray background (bg-neutral-50) - use for tags and metadata
 * - **outline**: White background with border (border-neutral-200) - use on colored backgrounds
 *
 * ## Sizes
 * - **sm** (default): Compact size for inline labels and tags - px-xxs, py-[3px], text-l2
 * - **lg**: Larger size for more prominent badges - px-xs, py-1, text-l1
 *
 * ## Use Cases
 * - Labels: Categorize content (e.g., "Featured", "New", "Popular")
 * - Tags: Show topics, keywords, or attributes
 * - Status: Display state information (e.g., "Active", "Draft", "Published")
 * - Counts: Show numerical data (e.g., "5 courses", "12 reviews")
 * - Filters: Interactive tags in search/filter interfaces
 *
 * @example
 * // Basic usage
 * <Badge>Featured</Badge>
 *
 * @example
 * // With specific variant and size
 * <Badge variant="light" size="lg">
 *   Trending
 * </Badge>
 *
 * @example
 * // As a link using asChild pattern
 * <Badge variant="outline" asChild>
 *   <a href="/category/education">Education</a>
 * </Badge>
 *
 * @example
 * // With icons (import from lucide-react)
 * import { Star } from 'lucide-react';
 * <Badge variant="primary">
 *   <Star className="w-3 h-3" /> Featured
 * </Badge>
 */
export const Badge = React.forwardRef<
  React.ElementRef<typeof ShadcnBadge>,
  BadgeProps
>(({ className, variant, children, size, ...props }, ref) => {
  return (
    <ShadcnBadge
      className={cn(badgeVariants({ variant, size }), className)}
      ref={ref}
      {...props}
    >
      {children}
    </ShadcnBadge>
  );
});

Badge.displayName = 'Badge';
